import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { update_referral } from "../../api/ControllApi";
import { useUser } from "../../context/LoginDetail";

const ReferralModal = ({ open, handleClose }) => {
  const [status, setStatus] = useState("idle"); // idle, updating, success, error
  const [errorMsg, setErrorMsg] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const { loadUser } = useUser();

  // Handle manual submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!referralCode.trim()) {
      setErrorMsg("Please enter a referral code.");
      setStatus("error");
      return;
    }

    setStatus("updating");
    setErrorMsg("");
    
    try {
      const res = await update_referral({ reference_id: referralCode.trim() });
      if (res?.success || res?.status === 1 || res?.status === 200) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMsg(res?.message || "Failed to update referral code.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err.response?.data?.message || 
        err.message || 
        "An error occurred while claiming your referral reward."
      );
    }
  };

  const onConfirm = async () => {
    if (status === "success") {
      await loadUser();
      handleClose();
      // Reset state after close
      setTimeout(() => {
        setStatus("idle");
        setErrorMsg("");
        setReferralCode("");
      }, 300);
    } else {
      handleClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 1 }
      }}
      onClose={handleClose}
    >
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: 700, display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
        Welcome to Geo Rental Farms
      </DialogTitle>
      
      <DialogContent sx={{ textAlign: 'center', py: 2 }}>
        {status === "idle" || status === "error" || status === "updating" ? (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your referral code
            </Typography>
            
            <TextField
              fullWidth
              label="Referral Code"
              variant="outlined"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              disabled={status === "updating"}
              size="small"
              sx={{ mb: 2 }}
              placeholder="Enter code here"
              error={status === "error"}
              helperText={status === "error" ? errorMsg : ""}
              autoFocus
            />

            {status === "updating" && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, my: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="caption">Validating code...</Typography>
              </Box>
            )}
          </Box>
        ) : null}

        {status === "success" && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
            <Typography variant="h6" color="success.main" fontWeight={700}>
              Updated Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your referral code has been applied. Enjoy your benefits!
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        {status === "success" ? (
          <Button 
            onClick={onConfirm} 
            variant="contained" 
            sx={{ 
              minWidth: 150, 
              backgroundColor: "#608900",
              borderRadius: 2,
              '&:hover': { backgroundColor: "#4a6900" }
            }}
          >
            Get Started
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={handleClose} 
              variant="outlined"
              disabled={status === "updating"}
              sx={{ minWidth: 100, borderRadius: 2 }}
            >
              Skip Now
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={status === "updating" || !referralCode.trim()}
              sx={{ 
                minWidth: 100, 
                backgroundColor: "#FEB821",
                borderRadius: 2,
                '&:hover': { backgroundColor: "#e0a31d" }
              }}
            >
              Submit
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReferralModal;
